import { useCallback, useEffect, useState } from "react";
import type {
  DisplayedIssue,
  IssueGroup,
  IssueStateAggregate,
  ValidationIssue,
} from "@/types/line-editor.types";

interface HistoryEntry {
  state: "pending" | "completed";
  order: number;
  snapshot: ValidationIssue;
}

interface InternalState {
  history: Map<string, HistoryEntry>;
  counter: number;
}

const GROUP_ORDER: Record<IssueGroup, number> = {
  header: 0,
  machines: 1,
  canvas: 2,
};

function initial(): InternalState {
  return { history: new Map(), counter: 0 };
}

/**
 * Duas issues são a MESMA afirmação? Comparação por VALOR, campo a campo.
 *
 * ⚠️ Por identidade não serve, e é o detalhe de que tudo depende: quem produz as issues
 * (`computeEditIssues` / `computeCreationIssues`) constrói objetos literais novos a cada cálculo,
 * então `existing.snapshot !== issue` é SEMPRE verdadeiro. Uma comparação por referência aqui
 * marcaria "mudou" em toda passagem e reabriria exatamente o loop que este arquivo agora previne.
 *
 * `ValidationIssue` é raso (e `target` também), então a comparação é explícita em vez de genérica:
 * um campo novo no tipo passa a exigir uma linha aqui, o que é um lembrete útil e não um custo.
 */
function sameIssue(a: ValidationIssue, b: ValidationIssue): boolean {
  return (
    a.id === b.id &&
    a.message === b.message &&
    a.detail === b.detail &&
    a.group === b.group &&
    a.severity === b.severity &&
    a.target?.kind === b.target?.kind &&
    a.target?.id === b.target?.id &&
    a.target?.field === b.target?.field
  );
}

/**
 * O histórico ESTÁVEL das pendências do editor: issue resolvida vira `completed` e MANTÉM a
 * posição, em vez de sumir da lista e fazer as de baixo pularem para cima.
 *
 * ⚠️ O REDUTOR PRECISA SER IDEMPOTENTE — devolver `prev` quando nada mudou. Não é otimização.
 *
 * O efeito depende de `activeIssues`, e quem o alimenta (`useUnifiedDraftIssues`) devolve um array
 * com identidade nova sempre que uma de suas queries está sem dados. Com o redutor produzindo
 * estado novo incondicionalmente, o ciclo se fecha: render → efeito → `setState` novo → render →
 * efeito… O React aborta em 50 updates encadeados com "Maximum update depth exceeded" e DESCARTA
 * as atualizações pendentes daquele ciclo — foi assim que a tela de edição de linha passou a
 * ignorar a inclusão de máquinas no canvas, com um erro no console que não mencionava nem canvas
 * nem máquina.
 *
 * Estabilizar a entrada (feito em `useUnifiedDraftIssues`) corta a mesma aresta do outro lado. As
 * duas correções são independentes de propósito: esta impede que QUALQUER dependência instável
 * futura reabra o loop.
 */
export function useStableIssueState(activeIssues: ValidationIssue[]): IssueStateAggregate {
  const [state, setState] = useState<InternalState>(initial);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- justificado acima
    setState((prev) => {
      const nextHistory = new Map(prev.history);
      let nextCounter = prev.counter;
      let changed = false;
      const activeIds = new Set(activeIssues.map((i) => i.id));

      for (const issue of activeIssues) {
        const existing = nextHistory.get(issue.id);
        if (!existing) {
          nextCounter += 1;
          nextHistory.set(issue.id, { state: "pending", order: nextCounter, snapshot: issue });
          changed = true;
        } else if (existing.state !== "pending" || !sameIssue(existing.snapshot, issue)) {
          // Reaberta, ou o texto da issue mudou. `order` é preservado: a posição na lista é o que
          // impede o painel de pular sob o olho de quem está corrigindo item a item.
          nextHistory.set(issue.id, { ...existing, state: "pending", snapshot: issue });
          changed = true;
        }
      }
      for (const [id, entry] of nextHistory.entries()) {
        if (!activeIds.has(id) && entry.state === "pending") {
          nextHistory.set(id, { ...entry, state: "completed" });
          changed = true;
        }
      }

      // O ponto inteiro do arquivo: nada mudou ⇒ MESMA referência ⇒ o React não re-renderiza ⇒ o
      // ciclo não se fecha.
      return changed ? { history: nextHistory, counter: nextCounter } : prev;
    });
  }, [activeIssues]);

  const reset = useCallback(() => {
    setState(initial());
  }, []);

  const displayed: DisplayedIssue[] = Array.from(state.history.values())
    .map((e) => ({ ...e.snapshot, state: e.state, order: e.order }))
    .sort((a, b) => {
      const groupDiff = GROUP_ORDER[a.group] - GROUP_ORDER[b.group];
      if (groupDiff !== 0) return groupDiff;
      return a.order - b.order;
    });
  const pendingCount = displayed.filter((d) => d.state === "pending").length;
  return { displayed, pendingCount, canSave: pendingCount === 0, reset };
}
